// Pure helpers for Settings → Plan & billing (billing B3). Prices come from
// GET /api/billing `catalog` (backend config/plans.js), in cents; nothing here
// hard-codes an amount.

const DAY_MS = 24 * 60 * 60 * 1000;

/** 19900 → '$199', 1933 → '$19.33', -1500 → '−$15'. */
export function formatMoney(cents) {
  const n = Number(cents) || 0;
  const whole = n % 100 === 0;
  const text = (Math.abs(n) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `−${text}` : text;
}

/** An ISO date (or Date) → 'December 1, 2026'. */
export function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/** Whole days left before `graceEndsAt` (never below 0), or null. */
export function graceDaysLeft(graceEndsAt, now = new Date()) {
  if (!graceEndsAt) return null;
  const ms = new Date(graceEndsAt).getTime() - new Date(now).getTime();
  return Math.max(0, Math.ceil(ms / DAY_MS));
}

const findItem = (catalog, key) => (catalog?.plans || []).find((p) => p.key === key)
  || (catalog?.addOns || []).find((a) => a.key === key) || null;

/** Price of one item for an interval ('month' | 'year'), in cents. */
export function itemPriceCents(catalog, key, interval, quantity = 1) {
  const item = findItem(catalog, key);
  if (!item) return 0;
  return (interval === 'year' ? item.yearlyCents : item.monthlyCents) * quantity;
}

/**
 * Total for a plan plus add-ons, per interval.
 * @param {object} catalog
 * @param {string} planKey
 * @param {Array<string|{key:string, quantity?:number}>} addOns
 * @param {'month'|'year'} interval
 */
export function totalCents(catalog, planKey, addOns, interval) {
  const list = (addOns || []).map((a) => (typeof a === 'string' ? { key: a, quantity: 1 } : a));
  return itemPriceCents(catalog, planKey, interval)
    + list.reduce((sum, a) => sum + itemPriceCents(catalog, a.key, interval, a.quantity || 1), 0);
}

/**
 * What paying yearly saves over twelve monthly payments (yearly = 10 ×
 * monthly, so "two months free").
 */
export function yearlySavingsCents(catalog, planKey, addOns) {
  return totalCents(catalog, planKey, addOns, 'month') * 12 - totalCents(catalog, planKey, addOns, 'year');
}

/** Add-ons a plan can take (those it does not already include). */
export function availableAddOns(catalog, planKey) {
  return (catalog?.addOns || []).filter((a) => !(a.includedIn || []).includes(planKey));
}

/**
 * Turn an add-on on or off in a list of keys. Turning one on drops the add-ons
 * it replaces (one texting size at a time).
 */
export function toggleAddOn(catalog, keys, key) {
  const current = keys || [];
  if (current.includes(key)) return current.filter((k) => k !== key);
  const item = (catalog?.addOns || []).find((a) => a.key === key);
  const replaces = (item && item.exclusiveWith) || [];
  return [...current.filter((k) => !replaces.includes(k)), key];
}

/** Keep only add-ons the plan can take (after the plan changes in the picker). */
export function addOnsForPlan(catalog, planKey, keys) {
  const allowed = new Set(availableAddOns(catalog, planKey).map((a) => a.key));
  return (keys || []).filter((k) => allowed.has(k));
}

/** 'month' → 'mo', 'year' → 'yr'. */
export const perInterval = (interval) => (interval === 'year' ? 'yr' : 'mo');

// Plans an owner can switch to in the app (B3b; backend services/billing.js
// SWITCH_PLANS). A shop on another plan can still change its interval.
export const SWITCH_PLANS = ['core', 'everything'];

/** Plans the switch offers: Core and Everything, plus the shop's own plan. */
export function switchPlanOptions(catalog, currentPlan) {
  return (catalog?.plans || []).filter((p) => SWITCH_PLANS.includes(p.key) || p.key === currentPlan);
}

/**
 * The founding discount (cents) for a plan and interval, when the shop holds
 * an unused founding grant (GET /billing `founding.eligible`, B6) and the
 * choice is the founding plan and interval (Core yearly); else 0.
 */
export function foundingDiscountCents(data, plan, interval) {
  const terms = data?.catalog?.founding;
  if (!data?.founding?.eligible || !terms) return 0;
  return plan === terms.plan && interval === terms.interval ? Number(terms.amountOffCents) || 0 : 0;
}
